/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { baseStyles, createId, state, querySlot } from '@cds/core/internal';
import styles from './breadcrumb.element.scss';
/**
 * ```typescript
 * import '@cds/core/breadcrumb/register.js';
 * ```
 *
 * ```html
 * <cds-breadcrumb aria-label="breadcrumb">
 *   <a href="/home" cds-text="link">Home</a>
 *   <a href="/parent" cds-text="link">Parent page</a>
 *   <span aria-current="page">Current page</span>
 * </cds-breadcrumb>
 * ```
 *
 * @element cds-breadcrumb
 * @cssprop --font-size
 * @cssprop --font-weight
 * @cssprop --letter-spacing
 * @cssprop --color
 */
export class CdsBreadcrumb extends LitElement {
    constructor() {
        super(...arguments);
        this.navItems = [];
    }
    render() {
        return html `
      <div class="private-host">
        <ol cds-layout="horizontal gap:xs align:vertical-center">
          ${this.slotNames.map(slotName => html `
              <li cds-layout="horizontal gap:xs align:vertical-center">
                <slot name="${slotName}"></slot>
                <span part="separator" aria-hidden="true">${this.separator}</span>
              </li>
            `)}
        </ol>
      </div>
      <slot @slotchange=${this.assignSlots}></slot>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.role = 'navigation';
    }
    get separator() {
        if (this.customSeparator) {
            const separatorClone = this.customSeparator.cloneNode(true);
            separatorClone.removeAttribute('slot');
            return separatorClone;
        }
        else {
            return html `/`;
        }
    }
    assignSlots() {
        this.navItems = Array.from(this.children)
            .filter(c => c.getAttribute('slot') !== 'cds-separator')
            .map(element => {
            element.setAttribute('slot', createId());
            return element;
        });
    }
    get slotNames() {
        return this.navItems.map(element => element.slot);
    }
    static get styles() {
        return [baseStyles, styles];
    }
}
__decorate([
    state({ type: Array })
], CdsBreadcrumb.prototype, "navItems", void 0);
__decorate([
    querySlot('[slot="cds-separator"]')
], CdsBreadcrumb.prototype, "customSeparator", void 0);
